import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { match, P } from 'ts-pattern';

import { L10n, L10nBase } from '@skylab-angular-shared/core';
import { CurrencyPipe } from '@skylab-angular-shared/pipes';
import { ButtonComponent, Menu, MenuDirective, MenuItem, MenuType, IconComponent } from '@skylab-angular-shared/ui';
import { CurrencyItem } from '@skylab-shared/business';

const SalePaymentStatus = {
    Paid: 1,
    PartiallyPaid: 2,
    Unpaid: 3
} as const;

type SalePaymentStatus = typeof SalePaymentStatus[keyof typeof SalePaymentStatus];

@Component({
    selector: 'skl-document-payment-button',
    template: `
        <skl-button
            [typeName]="btnType()"
            (click)="onBtnAction()">
            {{btnText()}}

            @if (showMenu()) {
                <skl-button
                    slot="group-end"
                    [typeName]="btnType()"
                    [skl-menu]="paymentMenu"
                >
                    <skl-icon name="angle-down"></skl-icon>
                </skl-button>
            }
        </skl-button>
    `,
    imports: [ButtonComponent, MenuDirective, IconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentPaymentButtonComponent {
    // inputs
    readonly wholeAmount = input.required<number>();
    readonly remainedAmount = input.required<number>();
    readonly currency = input.required<CurrencyItem>();
    readonly showPayments = input.required<() => void>();
    readonly payWholeAmount = input.required<() => void>();
    readonly showMenu = input<boolean, unknown>(true, { transform: booleanAttribute });

    // services
    readonly l10n = inject(L10n) as unknown as L10nBase;
    readonly currencyPipe = inject(CurrencyPipe);

    // derived state
    readonly paymentStatus = computed(() =>
        match([this.wholeAmount(), this.remainedAmount()])
            .with([P.number.negative(), P.select()], (remained, [whole]) =>
                match(remained)
                    .with(Math.abs(whole), () => SalePaymentStatus.Unpaid)
                    .with(0, () => SalePaymentStatus.Paid)
                    .otherwise(() => SalePaymentStatus.PartiallyPaid)
            )
            .with([P.any, P.number.lte(0)], () => SalePaymentStatus.Paid)
            .with([0, P.any], () => SalePaymentStatus.Paid)
            .with(P.when(([whole, remained]) => whole === remained), () => SalePaymentStatus.Unpaid)
            .with(P.when(([whole, remained]) => remained > 0 && remained < whole), () => SalePaymentStatus.PartiallyPaid)
            .otherwise(() => SalePaymentStatus.Paid)
    );

    readonly btnType = computed(() =>
        match(this.paymentStatus())
            .with(SalePaymentStatus.Paid, () => 'success' as const)
            .with(SalePaymentStatus.PartiallyPaid, () => 'warning' as const)
            .with(SalePaymentStatus.Unpaid, () => 'default' as const)
            .exhaustive()
    );

    readonly btnText = computed(() =>
        match(this.paymentStatus())
            .with(SalePaymentStatus.Paid, () =>
                this.l10n.instant('scenes.shared.control.document-payment-button.paid')
            )
            .with(SalePaymentStatus.PartiallyPaid, () => {
                const currency = this.currency();
                const remained = this.remainedAmount();
                const valueText = this.currencyPipe.transform(Math.abs(remained), { symbol: currency.symbol, precision: currency.precision });
                const labelKey = remained >= 0
                    ? 'scenes.shared.control.document-payment-button.remained'
                    : 'scenes.shared.control.document-payment-button.to-return';
                return `${this.l10n.instant(labelKey)}: ${valueText}`;
            })
            .with(SalePaymentStatus.Unpaid, () =>
                this.l10n.instant('scenes.shared.control.document-payment-button.unpaid')
            )
            .exhaustive()
    );

    readonly btnAction = computed(() =>
        match(this.paymentStatus())
            .with(SalePaymentStatus.Paid, SalePaymentStatus.PartiallyPaid, () => this.showPayments())
            .with(SalePaymentStatus.Unpaid, () => this.payWholeAmount())
            .exhaustive()
    );

    readonly paymentMenu = new Menu([
        new MenuItem({
            label: this.l10n.instant('scenes.shared.control.document-payment-button.menu.pay-full'),
            disabled: () => this.paymentStatus() === SalePaymentStatus.Paid,
            icon: 'icon-payments',
            click: () => {
                this.payWholeAmount()();
            }
        }),
        new MenuItem({
            label: this.l10n.instant('scenes.shared.control.document-payment-button.menu.show-payments'),
            disabled: false,
            icon: 'icon-payments-view',
            click: () => {
                this.showPayments()();
            }
        })
    ], {
        type: MenuType.Pointer,
        hideOnScroll: true,
    });

    onBtnAction(): void {
        this.btnAction()();
    }
}
